import {
  CatalogData,
  FilteredMaterialsByCategoriesI,
  Material,
  IProductConfigurationResult,
  IProductMaterialFilterEvent,
} from "ecom-types";

export type IUsePdpFilterOptions = {
  globalsSortOrder?: boolean;
};

export function useProductFilter(
  productData: Ref<CatalogData | null>,
  sizesSlugs: string[] = [],
  options?: IUsePdpFilterOptions,
) {
  const _options = options || {};
  const result = ref<IProductConfigurationResult>({});
  const additionalOptions = ref<IProductConfigurationResult>({});
  const clickOnAnyFilter = ref(false);
  const currentSku = ref(
    productData.value?.selected_config?.configuration_id ||
    productData.value?.variant_plp_image?.sku ||
      productData.value?.default_config?.vendor_configuration_id ||
      productData.value?.default_sku ||
      "",
  );

  const generalStore = useGeneralStore();

  const filteredMaterialsByCategories = computed(
    (): FilteredMaterialsByCategoriesI[] => {
      if (!productData.value) {
        return [];
      }

      const materialsByCategories =
        productData.value.material_categories?.reduce(
          (acc: FilteredMaterialsByCategoriesI[], el) => {
            const currentCategory =
              generalStore.bootstrap?.material_categories?.find(
                (it) => it.id === el.id,
              );

            if (currentCategory) {
              acc.push({
                ...currentCategory,
                materials:
                  productData.value?.materials?.reduce(
                    (acc: Material[], material) => {
                      if (material.material_category_id === el.id) {
                        acc.push({
                          ...material,
                        });
                      }
                      return acc;
                    },
                    [],
                  ) || [],
              });
            }
            return acc;
          },
          [],
        ) || [];

      return _options.globalsSortOrder
        ? materialsByCategories.sort(
            (a, b) => (a?.sort_order || 0) - (b?.sort_order || 0),
          )
        : materialsByCategories;
    },
  );

  function onFilterClick({ slug, item }: IProductMaterialFilterEvent) {
    if (!item) return;
    clickOnAnyFilter.value = true;
    result.value[slug] = item;
    setConfigurationValues();
  }

  function setConfigurationValues() {
    const newSku = Object.values(result.value).reduce((acc, el: any) => {
      return acc + "-" + el.sku;
    }, productData.value?.vendor_model as string);

    currentSku.value = newSku;
  }

  function onOptionsFilterClick({ item, slug }: IProductMaterialFilterEvent) {
    if (!item) return;
    clickOnAnyFilter.value = true;
    additionalOptions.value[slug] = item;
    setConfigurationValues();
  }

  function setResultData() {
    productData.value?.material_categories?.forEach((el) => {
      const currentMaterials = filteredMaterialsByCategories.value?.length
        ? filteredMaterialsByCategories.value?.find(
            (item) => item.id === el?.id,
          )?.materials
        : [];

      const material = currentMaterials?.find((item) =>
        productData.value?.default_config?.materials?.includes(item.id),
      );

      if (el?.slug && material && el.sku_enabled) {
        result.value[el?.slug] = material;
      } else if (currentMaterials?.length) {
        if (sizesSlugs.includes(el.slug)) {
          additionalOptions.value[el?.slug] = null;
        } else {
          additionalOptions.value[el?.slug] = currentMaterials?.[0] || null;
        }
      }
    });

    setConfigurationValues();
  }

  return {
    filteredMaterialsByCategories,
    result,
    additionalOptions,
    clickOnAnyFilter,
    currentSku,
    setResultData,
    onFilterClick,
    onOptionsFilterClick,
  };
}
